/**
 * Reading a trade out of the sentence the activity API sends.
 *
 * Activity arrives as one line of prose - "Neymar bought 4 Stay Below at 50.0¢
 * ($2.0)" - so the rows that show it used to be built by splitting the string on
 * spaces and printing whichever positions happened to line up. A market named in
 * two words, like "Moon Mission", shifted every position along and put half the
 * name where the price belonged. Reaching for the record's own fields instead is
 * no better: `market_name` holds the question the market asks, "Will Bitcoin
 * cross $150K in 2026?", not the outcome the shares are on.
 *
 * Picking the parts out by shape pins each value to what it looks like rather
 * than to where it falls, so a market name of any length lands in one piece and
 * the price is never mistaken for it.
 */

/** One activity row's trade, in the pieces a row needs to name them separately */
export interface ActivityTrade {
  /** Which way the trade went, as the sentence words it */
  action: 'bought' | 'sold';
  /** Shares traded, kept exactly as written so no rounding creeps in */
  shares: string;
  /** The outcome the shares are on, e.g. "Moon Mission" */
  outcome: string;
  /** The total the sentence reports, or null when it carries no figure */
  total: number | null;
  /** What one share came to, in cents: the total divided by the shares */
  pricePerShareCents: number | null;
}

/**
 * A figure as the sentences write them - 4, 0.5, 25.0, 1,200 - matched tightly
 * enough that a bracket or full stop sitting against it stays out of the number.
 * String.raw keeps the pattern readable: what is written here is the pattern.
 */
const NUMBER = String.raw`\d+(?:,\d{3})*(?:\.\d+)?`;

/**
 * The whole trade: quantity, outcome, the per-share price in cents, then the
 * total. "at" is optional on either side of the price, since older sentences put
 * it in front of the total instead. Because the outcome is matched lazily, the
 * shortest name that still leaves a price behind it wins, so a trailing "at" is
 * never taken into the name - while a name that contains one, like "Stay at
 * Home", still comes through whole.
 */
const TRADE = new RegExp(
  String.raw`\b(bought|sold)\s+(${NUMBER})\s+(.*?)\s*(?:shares?\b)?\s*(?:at\s+)?(${NUMBER})\s*¢\s*(?:at\s+)?\(?\s*\$\s*(${NUMBER})`,
  'i'
);
/** Quantity and outcome on their own, for a sentence missing one of the figures */
const QUANTITY = new RegExp(
  String.raw`\b(?:bought|sold)\s+(${NUMBER})\s+(.*?)(?:\s+shares?\b|\s+${NUMBER}\s*¢|\s+(?:at|@|for)\b|$)`,
  'i'
);
/** The per-share price, wherever the wording puts it */
const CENTS = new RegExp(String.raw`(${NUMBER})\s*¢`);
/** The total, which is the sentence's only dollar figure */
const TOTAL = new RegExp(String.raw`\$\s*(${NUMBER})`);

/**
 * @param text - The activity sentence, e.g. "Neymar bought 4 Stay Below at 50.0¢ ($2.0)"
 * @returns The trade it describes. A sentence that parses only in part still
 *          yields what could be read, so a row is never left blank.
 */
export function parseActivityTrade(text: string): ActivityTrade {
  const sentence = (text ?? '').trim();
  const trade = TRADE.exec(sentence);

  if (trade) {
    return buildTrade(trade[1], trade[2], trade[3], trade[4], trade[5]);
  }

  const quantity = QUANTITY.exec(sentence);
  const cents = CENTS.exec(sentence);
  const total = TOTAL.exec(sentence);
  const action = /\bsold\b/i.test(sentence) ? 'sold' : 'bought';

  return buildTrade(action, quantity?.[1] ?? '', quantity?.[2] ?? '', cents?.[1], total?.[1]);
}

function buildTrade(action: string, shares: string, outcome: string, cents?: string, total?: string): ActivityTrade {
  const sharesTraded = toNumber(shares);
  const spent = total === undefined ? null : toNumber(total);

  return {
    action: action.toLowerCase() === 'sold' ? 'sold' : 'bought',
    shares: shares.trim(),
    outcome: outcome.trim(),
    total: spent,
    pricePerShareCents: perShareCents(spent, sharesTraded, cents)
  };
}

/**
 * What one share came to, in cents. The total divided by the shares is the
 * figure the row shows, so the price and the total it sits beside always agree.
 * A sentence too incomplete to divide falls back to the price it states.
 */
function perShareCents(total: number | null, shares: number | null, cents?: string): number | null {
  if (total !== null && shares !== null && shares > 0) {
    return (total / shares) * 100;
  }

  return cents === undefined ? null : toNumber(cents);
}

function toNumber(value: string): number | null {
  const parsed = Number((value ?? '').replace(/,/g, ''));

  return Number.isFinite(parsed) && (value ?? '').trim() !== '' ? parsed : null;
}
